<template>
  <div class="congruence-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <marker id="tick-mark" markerWidth="6" markerHeight="16" refX="3" refY="8" orient="auto">
                 <line x1="3" y1="2" x2="3" y2="14" stroke="#f8fafc" stroke-width="2" />
              </marker>
              <marker id="tick-mark-2" markerWidth="10" markerHeight="16" refX="5" refY="8" orient="auto">
                 <line x1="3" y1="2" x2="3" y2="14" stroke="#f8fafc" stroke-width="2" />
                 <line x1="7" y1="2" x2="7" y2="14" stroke="#f8fafc" stroke-width="2" />
              </marker>
              <marker id="tick-mark-3" markerWidth="14" markerHeight="16" refX="7" refY="8" orient="auto">
                 <line x1="3" y1="2" x2="3" y2="14" stroke="#f8fafc" stroke-width="2" />
                 <line x1="7" y1="2" x2="7" y2="14" stroke="#f8fafc" stroke-width="2" />
                 <line x1="11" y1="2" x2="11" y2="14" stroke="#f8fafc" stroke-width="2" />
              </marker>
           </defs>

           <!-- Grid -->
           <g stroke="rgba(241, 245, 249, 0.05)" stroke-width="1.5">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- 基准三角形 ABC -->
           <g transform="translate(150, 150)">
              <polygon points="0,0 0,200 160,200" fill="rgba(56, 189, 248, 0.2)" stroke="#38bdf8" stroke-width="3" stroke-linejoin="round" />
              <text x="-20" y="-10" fill="#bae6fd" font-size="24" font-weight="900">A</text>
              <text x="-20" y="220" fill="#bae6fd" font-size="24" font-weight="900">B</text>
              <text x="175" y="220" fill="#bae6fd" font-size="24" font-weight="900">C</text>

              <!-- 已知条件高亮标注 (Source) -->
              <g stroke-linecap="round">
                 <!-- 边 AB (S1) -->
                 <line v-if="activeProps.includes('s1')" x1="0" y1="0" x2="0" y2="200" stroke="#facc15" stroke-width="6" marker-mid="url(#tick-mark)" />
                 <!-- 边 BC (S2) -->
                 <line v-if="activeProps.includes('s2')" x1="0" y1="200" x2="160" y2="200" stroke="#f43f5e" stroke-width="6" marker-mid="url(#tick-mark-2)" />
                 <!-- 边 AC (S3/Hypotenuse) -->
                 <line v-if="activeProps.includes('s3')" x1="0" y1="0" x2="160" y2="200" stroke="#10b981" stroke-width="6" marker-mid="url(#tick-mark-3)" />
                 
                 <!-- 角 B (直角/A1) -->
                 <path v-if="activeProps.includes('a1')" d="M 0 170 L 30 170 L 30 200" fill="none" stroke="#facc15" stroke-width="4" />
                 <!-- 角 C (A2) -->
                 <path v-if="activeProps.includes('a2')" d="M 120 200 A 40 40 0 0 0 135 168" fill="none" stroke="#f43f5e" stroke-width="4" />
                 <!-- 角 A (A3) -->
                 <path v-if="activeProps.includes('a3')" d="M 0 40 A 40 40 0 0 0 25 32" fill="none" stroke="#10b981" stroke-width="4" />
              </g>

              <!-- Placing marker-mid trick (needs separate paths for exact midpoints) -->
              <g stroke="none" fill="none">
                 <path v-if="activeProps.includes('s1')" d="M 0 0 L 0 200" marker-mid="url(#tick-mark)" />
                 <path v-if="activeProps.includes('s2')" d="M 0 200 L 160 200" marker-mid="url(#tick-mark-2)" />
                 <path v-if="activeProps.includes('s3')" d="M 0 0 L 160 200" marker-mid="url(#tick-mark-3)" />
              </g>
           </g>

           <!-- 待证三角形 A'B'C' (伴随动画) -->
           <g :transform="`translate(${450 - offset}, 150)`">
              <polygon points="0,0 0,200 160,200" fill="rgba(16, 185, 129, 0.2)" stroke="#10b981" stroke-width="3" stroke-linejoin="round" />
              <text x="-20" y="-10" fill="#6ee7b7" font-size="24" font-weight="900" :opacity="offset > 200 ? 0 : 1">A'</text>
              <text x="-20" y="220" fill="#6ee7b7" font-size="24" font-weight="900" :opacity="offset > 200 ? 0 : 1">B'</text>
              <text x="175" y="220" fill="#6ee7b7" font-size="24" font-weight="900" :opacity="offset > 200 ? 0 : 1">C'</text>

              <!-- 已知条件高亮标注 (Target) -->
              <g stroke-linecap="round">
                 <line v-if="activeProps.includes('s1')" x1="0" y1="0" x2="0" y2="200" stroke="#facc15" stroke-width="6" />
                 <line v-if="activeProps.includes('s2')" x1="0" y1="200" x2="160" y2="200" stroke="#f43f5e" stroke-width="6" />
                 <line v-if="activeProps.includes('s3')" x1="0" y1="0" x2="160" y2="200" stroke="#10b981" stroke-width="6" />
                 <path v-if="activeProps.includes('a1')" d="M 0 170 L 30 170 L 30 200" fill="none" stroke="#facc15" stroke-width="4" />
                 <path v-if="activeProps.includes('a2')" d="M 120 200 A 40 40 0 0 0 135 168" fill="none" stroke="#f43f5e" stroke-width="4" />
                 <path v-if="activeProps.includes('a3')" d="M 0 40 A 40 40 0 0 0 25 32" fill="none" stroke="#10b981" stroke-width="4" />
              </g>
              <g stroke="none" fill="none">
                 <path v-if="activeProps.includes('s1')" d="M 0 0 L 0 200" marker-mid="url(#tick-mark)" />
                 <path v-if="activeProps.includes('s2')" d="M 0 200 L 160 200" marker-mid="url(#tick-mark-2)" />
                 <path v-if="activeProps.includes('s3')" d="M 0 0 L 160 200" marker-mid="url(#tick-mark-3)" />
              </g>
           </g>

           <!-- 辅助叠合动画指示器 -->
           <path v-if="offset > 0" :d="`M ${450 - offset/2} 250 L ${150 + offset/2} 250`" fill="none" stroke="#94a3b8" stroke-width="3" stroke-dasharray="10,6" />
           <text v-if="offset >= 300" x="300" y="420" fill="#facc15" font-size="28" font-weight="900" text-anchor="middle" style="text-shadow: 0 4px 6px rgba(0,0,0,0.6)">完全重合 (叠合法)</text>
        </svg>

      </div>

      <!-- 右侧定理解析 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">平面几何公理推导</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            
            <div class="eq-box text-center">
               <div class="eq-display text-blue">当前判定定理</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>判定方法</span><span class="hl text-blue">{{ method.toUpperCase() }} 定理</span></div>
                  <div class="data-row"><span>已知条件一</span><span class="hl text-green">{{ cond1Text }}</span></div>
                  <div class="data-row"><span>已知条件二</span><span class="hl text-green">{{ cond2Text }}</span></div>
                  <div class="data-row"><span>已知条件三</span><span class="hl text-green">{{ cond3Text }}</span></div>
                  <div class="data-row" :style="{ background: offset >= 300 ? 'rgba(16,185,129,0.15)' : 'transparent' }">
                     <span>几何推论</span>
                     <span class="hl" :class="offset >= 300 ? 'text-green' : 'text-red'">{{ offset >= 300 ? '△ABC ≅ △A\'B\'C\'' : '准备推导...' }}</span>
                  </div>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
          <div class="control-grid-mini">
            <div class="s-item" style="grid-column: span 2;">
               <label>选择全等三角形判定定理</label>
               <el-radio-group v-model="method" size="small" @change="resetOffset" class="full-width-radio">
                 <el-radio-button label="sss">SSS (端点控制)</el-radio-button>
                 <el-radio-button label="sas">SAS (夹角刚性)</el-radio-button>
                 <el-radio-button label="asa">ASA (射线交点)</el-radio-button>
                 <el-radio-button label="aas">AAS (角角边)</el-radio-button>
                 <el-radio-button label="hl">HL (直角斜边)</el-radio-button>
               </el-radio-group>
            </div>
            
            <div class="s-item" style="grid-column: span 2;">
               <label>拖动使用叠合法证明</label>
               <el-slider v-model="offset" :min="0" :max="300" :step="10" size="small"></el-slider>
            </div>
          </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CongruenceViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      method: 'sss',
      offset: 0
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    topicIntro() {
       return '全等三角形判定：构成刚性三角形的极简要素条件';
    },
    activeFormula() {
       return '△ABC ≅ △A\'B\'C\'';
    },
    activeProps() {
       // s1(AB), s2(BC), s3(AC), a1(∠B/90d), a2(∠C), a3(∠A)
       if (this.method === 'sss') return ['s1', 's2', 's3'];
       if (this.method === 'sas') return ['s1', 'a1', 's2'];
       if (this.method === 'asa') return ['a1', 's2', 'a2'];
       if (this.method === 'aas') return ['a3', 'a2', 's2'];
       if (this.method === 'hl') return ['a1', 's1', 's3'];
       return [];
    },
    cond1Text() {
       if (this.method === 'sss') return '边 AB = A\'B\' (S)';
       if (this.method === 'sas') return '边 AB = A\'B\' (S)';
       if (this.method === 'asa') return '角 B = B\' = 90° (A)';
       if (this.method === 'aas') return '角 A = A\' (A)';
       if (this.method === 'hl') return '直角 B = B\' = 90°';
       return '';
    },
    cond2Text() {
       if (this.method === 'sss') return '边 BC = B\'C\' (S)';
       if (this.method === 'sas') return '角 B = B\' = 90° (A)';
       if (this.method === 'asa') return '夹边 BC = B\'C\' (S)';
       if (this.method === 'aas') return '角 C = C\' (A)';
       if (this.method === 'hl') return '直角边 AB = A\'B\' (L)';
       return '';
    },
    cond3Text() {
       if (this.method === 'sss') return '边 AC = A\'C\' (S)';
       if (this.method === 'sas') return '边 BC = B\'C\' (S)';
       if (this.method === 'asa') return '角 C = C\' (A)';
       if (this.method === 'aas') return '邻边 BC = B\'C\' (S)';
       if (this.method === 'hl') return '斜边 AC = A\'C\' (H)';
       return '';
    },
    tipDesc() {
       if (this.method === 'sss') return 'SSS (边边边)：给你三根长度固定的木棍，由于没有关节，首尾相连只能拼出唯一形状的三角形。这揭示了三角形极其重要的【稳定性】性质。';
       if (this.method === 'sas') return 'SAS (边角边)：固定两边的长度及其夹角，第三条边（开口处的连线）的长度就被铁死设定了，由此整个三角形唯一确定。';
       if (this.method === 'asa') return 'ASA (角边角)：在一条已知线段的两端，以两个已知角度发射射线，这两条射线必定交于平面上唯一坐标点，从而确定第三个顶点。';
       if (this.method === 'aas') return 'AAS (角角边)：已知两角，根据三角形内角和为180°，第三个角也即已知。此时问题退化归约成了 ASA 原理。这是极为精妙的推导联想！';
       if (this.method === 'hl') return 'HL (斜边直角边)：专属于直角三角形的特权法则！根据勾股定理 a² + b² = c²，已知斜边与一条直角边，第三边其实也已经锁死了，它实质上退化成了 SSS 定理。';
       return '全等判定定理。';
    }
  },
  methods: {
    detectMode() {
      // Could initialize based on KP, but they are generic here. User can click.
      this.offset = 0;
    },
    resetOffset() {
      this.offset = 0;
    }
  }
}
</script>

<style scoped lang="scss">
.congruence-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 15px; }

.text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-red { color: #f43f5e; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 18px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }

.full-width-radio {
  display: flex !important;
  width: 100%;
  flex-wrap: nowrap;
  ::v-deep {
    .el-radio-button {
      flex: 1;
      .el-radio-button__inner {
        width: 100%;
        padding: 9px 2px;
        font-size: 12px;
        white-space: nowrap;
      }
    }
  }
}

.control-grid-mini { 
  display: flex; 
  flex-direction: column; 
  gap: 15px; 
  .s-item { 
    width: 100%;
    label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } 
  } 
}
</style>
